 // @ts-nocheck
import './DevicePlaylists.scss';
import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import {arrayMoveImmutable} from 'array-move';
import { deviceStore, playlistStore, generalStore } from '../../store';
import { RESPONSE, ROUTES } from '../../js/_constants';
import Server from '../../js/config/_server';
import { apiPatch } from '../../js/api';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationModal from '../deleteConfirmationModal/DeleteConfirmationModal';
import helpers from '../../js/helpers';
import ItemCard from '../ItemCard/ItemCard';
import Loader from '../loader/Loader';
import { type Playlist } from '../../api/types/index';
import { findIndex, insert, isEmpty, propEq } from 'ramda';
import { type ActionButtonType } from '../ItemCard/ItemCard';

const NoPlaylistsText = () => {
    const { t } = useTranslation();
    return <p className="no-playlists">{t('device_detail_no_playlist')}</p>;
};

type PlaylistCardProps = {
    className?: string;
    playlist: Playlist;
    buttons: Array<ActionButtonType | Element | React.ReactNode>;
    rest?: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    >;
};

export const PlaylistCard: React.FC<PlaylistCardProps> = ({
    name,
    className,
    playlist,
    buttons,
    ...rest
}) => {
    const { t } = useTranslation();
    return (
        <>
            <ItemCard
                name={name}
                className={cx('b-device-playlists__playlist-card', className)}
                key={playlist.uuid + className}
                href={`${ROUTES.PLAYLISTS}/${playlist.uuid}`}
                thumbnail={playlist.thumbnail}
                icon="media-playlist"
                title={playlist.title}
                subtitle={`${
                    playlist.language || 'EN'
                } - ${helpers.humanDuration(playlist.duration)} Min`}
                description={playlist.description}
                footer={`${t('playlist_last_edit')} : ${
                    playlist.updatedAt
                        ? helpers.humanDate(playlist.updatedAt)
                        : ''
                }`}
                buttons={buttons}
                {...rest}
            />
        </>
    );
};

type DevicePlaylistsProps = {
    items: Playlist[];
    iri: string;
    isRecentPlaylists?: boolean;
};

const DevicePlaylists = ({
    items,
    iri,
    isRecentPlaylists
}: DevicePlaylistsProps) => {
    const { t } = useTranslation();

    const { updateDevice, loading } = deviceStore();
    const { setPlaylistId, playlistId } = playlistStore();
    const { setModalOpen, isModalOpen } = generalStore();

    const [assignedPlaylists, setAssignedPlaylists] = useState<Playlist[]>([]);

    useEffect(() => {
        setAssignedPlaylists(items);
    }, [items]);

    const onCancelDeletion = () => {
        setModalOpen(false);
        setIsPlaylistDeletion(false);
    };

    const playlistIsAlreadyAssigned = (playlistId: string) =>
        findIndex(propEq(playlistId, '@id'), assignedPlaylists) > -1;

    const [isSorting, setIsSorting] = useState(false);

    const handleMove = (oldIndex, newIndex) => {

        setIsSorting(true);
        const currentlyDraggedPlaylist = assignedPlaylists[oldIndex];

        let nextAssignedPlaylists: Playlist[];

        if (playlistIsAlreadyAssigned(currentlyDraggedPlaylist['@id'])) {
            nextAssignedPlaylists  = arrayMoveImmutable(
                assignedPlaylists,
                oldIndex,
                newIndex
            );
        } else {
            nextAssignedPlaylists = insert(
                newIndex,
                currentlyDraggedPlaylist,
                assignedPlaylists
            );
        }
        
        const url = Server.baseUrl + iri;

        const playlistsToAssign = nextAssignedPlaylists.map((item,index) => { return { position:index + 1, contentPlaylist:item.contentPlaylist[RESPONSE.ID] }});
        const options = {
            body: { playlists: playlistsToAssign }
        };

        setAssignedPlaylists(nextAssignedPlaylists);

        apiPatch(url, options).then(() => {
            setIsSorting(false);
        });
    };

    const [isDeleting, setIsDeleting] = useState(false);

    const [isPlaylistDeletion, setIsPlaylistDeletion] = useState(false);
    
    const deletePlaylist = async playlistId => {
        setIsDeleting(true);
        const newList = assignedPlaylists.filter(
            item => item.contentPlaylist[RESPONSE.ID] !== playlistId
        );

        setAssignedPlaylists(newList);

        const newPlaylistsToAssign = newList.map((item,idx) => { return { position: idx + 1, contentPlaylist:item.contentPlaylist[RESPONSE.ID] }});

        await updateDevice({
            id: iri,
            body: { playlists: newPlaylistsToAssign }
        });


        setIsDeleting(false);
        setModalOpen(false);
        setIsPlaylistDeletion(false);
    };

    const onDeleteHandler = id => {
        setModalOpen(true);
        setIsPlaylistDeletion(true);
        setPlaylistId(id);
    };

    const generatePlaylistActionButtons = (ele, index, itemsLength) => [
        <div className="order-arrows">
            {index !== 0 && <a className="order-arrow" onClick={() => handleMove(index, index - 1)} data-testid={`move-up-${index + 1}`}>&#x25B2;</a>}
            {index !== itemsLength -1 && <a className="order-arrow" onClick={() => handleMove(index, index + 1)} data-testid={`move-down-${index + 1}`}>&#x25BC;</a>}
         </div>,
        {
            icon: 'remove',
            name: `remove-playlist-${index + 1}`,
            onClick: () => onDeleteHandler(ele.contentPlaylist[RESPONSE.ID])
        }
    ];

    return (
        <>
            <div
                className={cx('b-device-playlists', {
                    'is-disabled': isSorting || loading
                })}
            >
                    <h3 className="playlists-header">
                    {isRecentPlaylists ? t('device_detail_recently_assigned_playlists') : t('device_assigned_playlists') }
                    </h3>
                    {(isSorting || loading) && <Loader />}
                    {isEmpty(assignedPlaylists) && <NoPlaylistsText />}
                    {assignedPlaylists.map((playlist, index) => (
                        <PlaylistCard
                            name={`assigned-playlist-card-${index + 1}`}
                            playlist={playlist.contentPlaylist}
                            buttons={!isRecentPlaylists && generatePlaylistActionButtons(
                                playlist, index, assignedPlaylists.length
                            )}
                        />
                    ))}
            </div>
            <DeleteConfirmationModal
                show={isModalOpen && isPlaylistDeletion}
                onClose={onCancelDeletion}
                onCancel={onCancelDeletion}
                onAction={() => deletePlaylist(playlistId)}
                localeAction={t('delete')}
                localeCancel={t('cancel')}
                isLoading={isDeleting}
            >
                <h4>{t('delete_playlist_device')}</h4>
            </DeleteConfirmationModal>
        </>
    );
};

export default DevicePlaylists;